import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Shield, Lock, Eye, EyeOff, CheckCircle, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";



import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Enter email, 2: Answer questions, 3: Success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  const [email, setEmail] = useState("");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({ answer1: "", answer2: "" });
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.post(`${API}/users/forgot-password/init`, {
        email: email.trim()
      });

      setQuestions(res.data.questions);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || "Email not found or an error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSecuritySubmit = async (e) => {
    e.preventDefault();
    
    if (!answers.answer1.trim() || !answers.answer2.trim()) {
      setError("Please answer both security questions");
      return;
    }

    if (!newPassword || newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/users/forgot-password/verify`, {
        email: email.trim(),
        question1: questions[0].question,
        answer1: answers.answer1.trim(),
        question2: questions[1].question,
        answer2: answers.answer2.trim(),
        new_password: newPassword
      });

      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || "Security answers incorrect or an error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#050505]">
      
      
      
      <div className="pt-8 pb-16 px-4">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-white/10 p-8"
          >
            {step === 1 && (
              <>
                {/* Step 1: Enter Email */}
                {/* Back Button */}
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-6 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>

                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-white" />
                  </div>
                  <h1 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">
                    Reset Password
                  </h1>
                  <p className="text-zinc-500 text-sm mt-2">
                    Enter your email to receive security questions
                  </p>
                </div>

                {error && (
                  <div className="mb-6 p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(""); }}
                        placeholder="Enter your email"
                        className="w-full bg-zinc-800 border border-white/10 text-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-white/30"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-sm py-3 h-auto rounded-none mt-4"
                  >
                    {loading ? "Verifying..." : "Continue"}
                  </Button>
                </form>

                <p className="text-center text-zinc-500 text-sm mt-6">
                  <Link to="/login-user" className="text-white hover:underline flex items-center justify-center gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </Link>
                </p>
              </>
            )}

            {step === 2 && (
              <>
                {/* Step 2: Answer Security Questions */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h1 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">
                    Security Questions
                  </h1>
                  <p className="text-zinc-500 text-sm mt-2">
                    Answer your security questions to reset password
                  </p>
                </div>

                {error && (
                  <div className="mb-6 p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSecuritySubmit} className="space-y-5">
                  {questions.map((q, idx) => (
                    <div key={idx}>
                      <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                        Question {idx + 1}
                      </label>
                      <p className="text-white text-sm mb-2">{q.question}</p>
                      <input
                        type="text"
                        value={answers[`answer${idx + 1}`]}
                        onChange={(e) => setAnswers({ ...answers, [`answer${idx + 1}`]: e.target.value })}
                        placeholder="Your answer"
                        className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-white/30"
                      />
                    </div>
                  ))}

                  <div className="border-t border-white/10 pt-5 mt-5">
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Min 8 characters"
                        className="w-full bg-zinc-800 border border-white/10 text-white pl-10 pr-10 py-3 text-sm focus:outline-none focus:border-white/30"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter password"
                        className="w-full bg-zinc-800 border border-white/10 text-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-white/30"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-sm py-3 h-auto rounded-none mt-4"
                  >
                    {loading ? "Resetting..." : "Reset Password"}
                  </Button>
                </form>

                <p className="text-center text-zinc-500 text-sm mt-6">
                  <button 
                    onClick={() => { setStep(1); setError(""); }}
                    className="text-white hover:underline flex items-center justify-center gap-2 mx-auto"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Use a different email
                  </button>
                </p>
              </>
            )}

            {step === 3 && (
              <>
                {/* Step 3: Success */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h1 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white mb-4">
                    Password Reset!
                  </h1>
                  <p className="text-zinc-400 text-sm mb-8">
                    Your password has been successfully reset. You can now sign in with your new password.
                  </p>
                  <Button
                    onClick={() => navigate("/login-user")}
                    className="w-full bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-sm py-3 h-auto rounded-none"
                  >
                    Sign In Now
                  </Button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>

      
    </main>
  );
};

export default ForgotPasswordPage;
